/**
 * Recommendations Page
 *
 * Curated list of recommended projects, exchanges, applications,
 * Twitter accounts, and blog posts for the crypto community.
 */

import { useState } from 'react';
import { Icons } from '../components/Icons';

type TabType = 'projects' | 'exchanges' | 'apps' | 'twitter' | 'blogs';

// Recommendation data interfaces
interface BaseRecommendation {
  id: string;
  name: string;
  description: string;
  image?: string;
  url: string;
  tags: string[];
  featured?: boolean;
}

interface ProjectRecommendation extends BaseRecommendation {
  category: string;
}

interface ExchangeRecommendation extends BaseRecommendation {
  type: 'cex' | 'dex';
  fees?: string;
}

interface AppRecommendation extends BaseRecommendation {
  platform: 'web' | 'mobile' | 'desktop' | 'all';
}

interface TwitterRecommendation extends BaseRecommendation {
  handle: string;
  followers?: string;
}

interface BlogRecommendation extends BaseRecommendation {
  author?: string;
  date?: string;
}

// Sample data - Projects (Wallets & Security)
const PROJECTS: ProjectRecommendation[] = [
  {
    id: 'ledger',
    name: 'Ledger',
    description: 'Hardware wallet leader mondial. Sécurisez vos cryptos avec une solution de stockage à froid certifiée.',
    url: 'https://ledger.com',
    category: 'Hardware Wallet',
    tags: ['Sécurité', 'Cold Storage', 'Multi-chain'],
    featured: true,
  },
  {
    id: 'trezor',
    name: 'Trezor',
    description: 'Premier hardware wallet open-source. Protégez vos actifs avec une sécurité de niveau bancaire.',
    url: 'https://trezor.io',
    category: 'Hardware Wallet',
    tags: ['Open Source', 'Sécurité', 'Bitcoin'],
    featured: true,
  },
  {
    id: 'citadel-wallet',
    name: 'Citadel Wallet',
    description: 'Wallet multi-chain sécurisé avec interface intuitive. Gérez tous vos actifs en un seul endroit.',
    url: 'https://citadelwallet.io',
    category: 'Software Wallet',
    tags: ['Multi-chain', 'DeFi', 'Sécurité'],
  },
  {
    id: 'ssp-wallet',
    name: 'SSP Wallet',
    description: 'Portefeuille Flux officiel avec support multi-assets. Intégration native avec l\'écosystème Flux.',
    url: 'https://runonflux.io/ssp-wallet',
    category: 'Software Wallet',
    tags: ['Flux', 'Multi-chain', 'Staking'],
    featured: true,
  },
  {
    id: 'flux',
    name: 'Flux',
    description: 'Infrastructure cloud décentralisée pour les applications Web 3.0. Hébergez vos applications sur un réseau mondial de nodes.',
    url: 'https://runonflux.io',
    category: 'Infrastructure',
    tags: ['Cloud', 'Décentralisé', 'Web3'],
  },
  {
    id: 'thorchain',
    name: 'THORChain',
    description: 'Protocole de liquidité cross-chain décentralisé. Échangez des actifs natifs entre différentes blockchains.',
    url: 'https://thorchain.org',
    category: 'DeFi',
    tags: ['Cross-chain', 'DEX', 'Liquidity'],
  },
];

// Sample data - Exchanges
const EXCHANGES: ExchangeRecommendation[] = [
  {
    id: 'cryptocom',
    name: 'Crypto.com',
    description: 'Plateforme complète avec carte Visa crypto, app mobile intuitive et exchange compétitif. Cashback jusqu\'à 8%.',
    url: 'https://crypto.com',
    type: 'cex',
    fees: '0.4%',
    tags: ['Carte Visa', 'Mobile', 'Cashback'],
    featured: true,
  },
  {
    id: 'bybit',
    name: 'Bybit',
    description: 'Exchange de dérivés crypto de premier plan. Trading futures et perpétuels avec effet de levier jusqu\'à 100x.',
    url: 'https://bybit.com',
    type: 'cex',
    fees: '0.1%',
    tags: ['Futures', 'Derivatives', 'Trading'],
    featured: true,
  },
  {
    id: 'mtpelerin',
    name: 'Mt Pelerin',
    description: 'Passerelle fiat-crypto suisse régulée. Achetez des cryptos par virement SEPA sans KYC jusqu\'à 1000€.',
    url: 'https://mtpelerin.com',
    type: 'cex',
    fees: '1.3%',
    tags: ['Fiat', 'Suisse', 'No KYC'],
    featured: true,
  },
  {
    id: 'thorswap',
    name: 'THORSwap',
    description: 'Interface pour THORChain. Swaps cross-chain natifs sans bridges ni intermédiaires.',
    url: 'https://thorswap.finance',
    type: 'dex',
    tags: ['Cross-chain', 'DEX', 'Native'],
  },
  {
    id: 'uniswap-ex',
    name: 'Uniswap',
    description: 'DEX leader sur Ethereum. Échangez sans KYC avec votre propre wallet.',
    url: 'https://app.uniswap.org',
    type: 'dex',
    fees: '0.3%',
    tags: ['DEX', 'Non-custodial', 'DeFi'],
  },
  {
    id: 'binance',
    name: 'Binance',
    description: 'La plus grande plateforme d\'échange crypto au monde par volume. Large sélection de paires.',
    url: 'https://binance.com',
    type: 'cex',
    fees: '0.1%',
    tags: ['Trading', 'Futures', 'Staking'],
  },
];

// Sample data - Apps
const APPS: AppRecommendation[] = [
  {
    id: 'brave',
    name: 'Brave Browser',
    description: 'Navigateur web respectueux de la vie privée avec bloqueur de pubs intégré. Gagnez des BAT en naviguant.',
    url: 'https://brave.com',
    platform: 'all',
    tags: ['Privacy', 'Browser', 'BAT Rewards'],
    featured: true,
  },
  {
    id: 'protonmail',
    name: 'ProtonMail',
    description: 'Service email chiffré de bout en bout basé en Suisse. Protégez vos communications crypto sensibles.',
    url: 'https://proton.me/mail',
    platform: 'all',
    tags: ['Email', 'Privacy', 'Encryption'],
    featured: true,
  },
  {
    id: 'protonvpn',
    name: 'ProtonVPN',
    description: 'VPN sécurisé sans logs basé en Suisse. Protégez votre connexion lors de vos transactions crypto.',
    url: 'https://protonvpn.com',
    platform: 'all',
    tags: ['VPN', 'Privacy', 'Security'],
    featured: true,
  },
  {
    id: 'tradingview',
    name: 'TradingView',
    description: 'Plateforme de charting professionnelle. Analysez les marchés avec des outils avancés.',
    url: 'https://tradingview.com',
    platform: 'web',
    tags: ['Charts', 'Analyse', 'Trading'],
  },
  {
    id: 'coingecko',
    name: 'CoinGecko',
    description: 'Agrégateur de données crypto. Suivez les prix, volumes et informations de milliers de tokens.',
    url: 'https://coingecko.com',
    platform: 'all',
    tags: ['Data', 'Prix', 'Research'],
  },
  {
    id: 'debank',
    name: 'DeBank',
    description: 'Dashboard DeFi multi-chain. Visualisez tous vos protocoles DeFi en un seul endroit.',
    url: 'https://debank.com',
    platform: 'web',
    tags: ['DeFi', 'Portfolio', 'Multi-chain'],
  },
];

// Sample data - Twitter accounts
const TWITTER: TwitterRecommendation[] = [
  {
    id: 'vitalik',
    name: 'Vitalik Buterin',
    handle: '@VitalikButerin',
    description: 'Co-fondateur d\'Ethereum. Partage ses réflexions sur la technologie blockchain et la crypto.',
    url: 'https://twitter.com/VitalikButerin',
    followers: '5.2M',
    tags: ['Ethereum', 'Tech', 'Thought Leader'],
    featured: true,
  },
  {
    id: 'cz',
    name: 'CZ Binance',
    handle: '@caborek_',
    description: 'Fondateur de Binance. Actualités sur l\'écosystème Binance et le marché crypto.',
    url: 'https://twitter.com/caborek_',
    followers: '8.9M',
    tags: ['Binance', 'Trading', 'News'],
  },
  {
    id: 'cobie',
    name: 'Cobie',
    handle: '@coaborek_',
    description: 'Trader et commentateur crypto influent. Analyses de marché et insights.',
    url: 'https://twitter.com/coaborek_',
    followers: '700K',
    tags: ['Trading', 'Analysis', 'Alpha'],
    featured: true,
  },
  {
    id: 'cryptobanterg',
    name: 'Crypto Banter',
    handle: '@crypto_banter',
    description: 'Émission de trading crypto en direct. Analyses quotidiennes du marché.',
    url: 'https://twitter.com/crypto_banter',
    followers: '600K',
    tags: ['Trading', 'Live', 'Education'],
  },
  {
    id: 'degentrader',
    name: 'Degen Spartan',
    handle: '@DegenSpartan',
    description: 'Trader DeFi et commentateur. Insights sur les nouvelles opportunités DeFi.',
    url: 'https://twitter.com/DegenSpartan',
    followers: '200K',
    tags: ['DeFi', 'Trading', 'Alpha'],
  },
  {
    id: 'theblock',
    name: 'The Block',
    handle: '@TheBlock__',
    description: 'Média crypto de référence. Actualités, analyses et données de marché.',
    url: 'https://twitter.com/TheBlock__',
    followers: '500K',
    tags: ['News', 'Research', 'Data'],
  },
];

// Sample data - Blogs
const BLOGS: BlogRecommendation[] = [
  {
    id: 'vitalik-blog',
    name: 'Vitalik\'s Blog',
    description: 'Blog personnel de Vitalik Buterin. Articles techniques approfondis sur Ethereum et la crypto.',
    url: 'https://vitalik.eth.limo',
    author: 'Vitalik Buterin',
    tags: ['Ethereum', 'Tech', 'Philosophy'],
    featured: true,
  },
  {
    id: 'bankless',
    name: 'Bankless',
    description: 'Newsletter et podcast sur la finance décentralisée. Guides et analyses pour naviguer le monde DeFi.',
    url: 'https://bankless.com',
    tags: ['DeFi', 'Education', 'News'],
    featured: true,
  },
  {
    id: 'messari',
    name: 'Messari Research',
    description: 'Recherche et analyses professionnelles sur les cryptos. Rapports détaillés sur les projets.',
    url: 'https://messari.io/research',
    tags: ['Research', 'Data', 'Analysis'],
  },
  {
    id: 'thedefiant',
    name: 'The Defiant',
    description: 'Actualités et analyses DeFi quotidiennes. Couvre l\'écosystème DeFi en profondeur.',
    url: 'https://thedefiant.io',
    tags: ['DeFi', 'News', 'Analysis'],
  },
  {
    id: 'paradigm',
    name: 'Paradigm Research',
    description: 'Blog de recherche du fonds Paradigm. Articles techniques sur les mécanismes crypto.',
    url: 'https://paradigm.xyz/writing',
    tags: ['Research', 'Tech', 'DeFi'],
  },
  {
    id: 'delphi',
    name: 'Delphi Digital',
    description: 'Recherche institutionnelle sur les crypto-actifs. Analyses de marché approfondies.',
    url: 'https://delphidigital.io',
    tags: ['Research', 'Institutional', 'Data'],
  },
];

// Tab configuration
const TABS = [
  { id: 'projects', label: 'Projets', icon: Icons.Rocket },
  { id: 'exchanges', label: 'Exchanges', icon: Icons.DollarSign },
  { id: 'apps', label: 'Applications', icon: Icons.Smartphone },
  { id: 'twitter', label: 'Twitter', icon: Icons.Twitter },
  { id: 'blogs', label: 'Blogs', icon: Icons.FileText },
] as const;

// Card component for recommendations
function RecommendationCard({
  item,
  type,
}: {
  item: BaseRecommendation;
  type: TabType;
}) {
  const getTypeInfo = () => {
    switch (type) {
      case 'exchanges':
        const exchange = item as ExchangeRecommendation;
        return (
          <span style={{
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '11px',
            fontWeight: 600,
            background: exchange.type === 'cex' ? 'rgba(59, 130, 246, 0.15)' : 'rgba(34, 197, 94, 0.15)',
            color: exchange.type === 'cex' ? '#3B82F6' : '#22C55E',
            textTransform: 'uppercase',
          }}>
            {exchange.type}
          </span>
        );
      case 'apps':
        const app = item as AppRecommendation;
        return (
          <span style={{
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '11px',
            fontWeight: 600,
            background: 'rgba(139, 92, 246, 0.15)',
            color: '#8B5CF6',
          }}>
            {app.platform === 'all' ? 'Multi-platform' : app.platform}
          </span>
        );
      case 'twitter':
        const twitter = item as TwitterRecommendation;
        return (
          <span style={{ fontSize: '13px', color: 'var(--accent)' }}>
            {twitter.handle}
          </span>
        );
      case 'projects':
        const project = item as ProjectRecommendation;
        return (
          <span style={{
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '11px',
            fontWeight: 600,
            background: 'var(--border)',
            color: 'var(--text)',
          }}>
            {project.category}
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <a
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'block',
        background: 'var(--card)',
        borderRadius: '12px',
        border: '1px solid var(--border)',
        padding: '20px',
        textDecoration: 'none',
        color: 'inherit',
        transition: 'transform 0.2s, box-shadow 0.2s, border-color 0.2s',
        position: 'relative',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)';
        e.currentTarget.style.borderColor = 'var(--accent)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.borderColor = 'var(--border)';
      }}
    >
      {item.featured && (
        <div style={{
          position: 'absolute',
          top: '12px',
          right: '12px',
          padding: '4px 8px',
          borderRadius: '4px',
          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
          color: 'white',
          fontSize: '10px',
          fontWeight: 700,
          textTransform: 'uppercase',
        }}>
          Recommandé
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '12px' }}>
        <div style={{
          width: '44px',
          height: '44px',
          borderRadius: '10px',
          background: 'linear-gradient(135deg, var(--accent) 0%, #8b5cf6 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}>
          <span style={{ color: 'white', fontSize: '18px', fontWeight: 700 }}>
            {item.name.charAt(0)}
          </span>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{ fontSize: '16px', fontWeight: 700, marginBottom: '2px' }}>{item.name}</h3>
          {getTypeInfo()}
        </div>
      </div>

      <p style={{
        fontSize: '13px',
        color: 'var(--muted)',
        lineHeight: 1.5,
        marginBottom: '12px',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
      }}>
        {item.description}
      </p>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
        {item.tags.slice(0, 3).map(tag => (
          <span key={tag} style={{
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '11px',
            background: 'var(--hover)',
            color: 'var(--text)',
          }}>
            {tag}
          </span>
        ))}
      </div>

      <div style={{
        marginTop: '12px',
        paddingTop: '12px',
        borderTop: '1px solid var(--border)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <span style={{ fontSize: '12px', color: 'var(--accent)', fontWeight: 500 }}>
          Visiter
        </span>
        <Icons.ExternalLink size={14} style={{ color: 'var(--muted)' }} />
      </div>
    </a>
  );
}

export function Recommendations() {
  const [activeTab, setActiveTab] = useState<TabType>('projects');
  const [showFeaturedOnly, setShowFeaturedOnly] = useState(false);

  // Get data based on active tab
  const getData = (): BaseRecommendation[] => {
    let data: BaseRecommendation[];
    switch (activeTab) {
      case 'projects':
        data = PROJECTS;
        break;
      case 'exchanges':
        data = EXCHANGES;
        break;
      case 'apps':
        data = APPS;
        break;
      case 'twitter':
        data = TWITTER;
        break;
      case 'blogs':
        data = BLOGS;
        break;
      default:
        data = [];
    }
    return showFeaturedOnly ? data.filter(item => item.featured) : data;
  };

  const items = getData();

  return (
    <div style={{ paddingBottom: '40px' }}>
      {/* Header */}
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{
          fontSize: '28px',
          fontWeight: 700,
          marginBottom: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <Icons.Star size={32} style={{ color: 'var(--accent)' }} />
          Recommandations
        </h1>
        <p style={{ fontSize: '15px', color: 'var(--muted)', maxWidth: '600px' }}>
          Notre sélection des meilleurs projets, exchanges, applications et ressources pour naviguer l'écosystème crypto.
        </p>
      </div>

      {/* Tab Navigation */}
      <div style={{
        display: 'flex',
        gap: '4px',
        background: 'var(--border)',
        padding: '4px',
        borderRadius: '12px',
        marginBottom: '20px',
        width: 'fit-content',
        flexWrap: 'wrap',
      }}>
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id as TabType)}
            style={{
              padding: '10px 16px',
              fontSize: '14px',
              fontWeight: 600,
              background: activeTab === id ? 'var(--accent)' : 'transparent',
              color: activeTab === id ? 'white' : 'var(--text)',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s',
            }}
          >
            <Icon size={16} />
            {label}
          </button>
        ))}
      </div>

      {/* Filter */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '24px',
      }}>
        <button
          onClick={() => setShowFeaturedOnly(!showFeaturedOnly)}
          style={{
            padding: '8px 16px',
            borderRadius: '8px',
            border: showFeaturedOnly ? 'none' : '1px solid var(--border)',
            background: showFeaturedOnly ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' : 'transparent',
            color: showFeaturedOnly ? 'white' : 'var(--text)',
            fontSize: '13px',
            fontWeight: 500,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            transition: 'all 0.2s',
          }}
        >
          <Icons.Star size={14} />
          Recommandés uniquement
        </button>
        <span style={{ fontSize: '13px', color: 'var(--muted)' }}>
          {items.length} résultat{items.length > 1 ? 's' : ''}
        </span>
      </div>

      {/* Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '20px',
      }}>
        {items.map(item => (
          <RecommendationCard key={item.id} item={item} type={activeTab} />
        ))}
      </div>

      {items.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          color: 'var(--muted)',
        }}>
          <Icons.Inbox size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
          <p>Aucune recommandation trouvée avec ce filtre</p>
        </div>
      )}

      {/* Disclaimer */}
      <div style={{
        marginTop: '40px',
        padding: '16px',
        background: 'var(--hover)',
        borderRadius: '12px',
        border: '1px solid var(--border)',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <Icons.Info size={20} style={{ color: 'var(--muted)', flexShrink: 0, marginTop: '2px' }} />
          <div>
            <p style={{ fontSize: '13px', color: 'var(--muted)', lineHeight: 1.6, margin: 0 }}>
              <strong>Disclaimer :</strong> Ces recommandations sont fournies à titre informatif uniquement et ne constituent pas des conseils financiers.
              Faites toujours vos propres recherches (DYOR) avant d'investir ou d'utiliser un service.
              Nous ne sommes pas affiliés à ces projets sauf indication contraire.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Recommendations;
